// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductMiniCard, { ProductMiniCardStyles } from "@guestsupply/App/Components/Product/ProductMiniCard";
import XngCarousel, { SwiperStyle } from "@guestsupply/App/Components/XngCarousel/XngCarousel";
import xItemReplacements from "@guestsupply/App/Handlers/XNG/ItemReplacements";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { SectionTitle } from "@guestsupply/App/Widgets/DesignSystem/DesignSystem.helpers";
import { SelectPresentationProps } from "@guestsupply/MobiusOverrides/Select/Select";
import { stringToKeyGenerator } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Select, Tab, TabGroup } from "@insite/mobius/index";
import { TabPresentationProps } from "@insite/mobius/Tab";
import { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum tabFields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    widgetPosition = "widgetPosition",
}

const enum fields {
    title = "title",
    tabs = "tabs",
    showImage = "showImage",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    slidesPerView = "slidesPerView",
}

interface TabsProps {
    fields: {
        [tabFields.title]: string;
        [tabFields.carouselType]: string;
        [tabFields.displayProductsFrom]: string;
        [tabFields.selectedCategoryIds]: string[];
        [tabFields.relatedProductType]: string;
        [tabFields.numberOfProductsToDisplay]: number;
        [tabFields.seedWithManuallyAssigned]: string;
        [tabFields.widgetPosition]: number;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.slidesPerView]: number;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const tabProducts: { title: string; products: string[] }[] = [];
    const allProducts = ownProps.fields.tabs.reduce((acc, tab, index) => {
        const products = getProductsForProductInfoList(state, `${ownProps.id}-${tab.fields.title}-${index}`);
        if (products) {
            const productNumbers = products.map(product => product.productNumber);
            tabProducts.push({ title: tab.fields.title, products: productNumbers });
            return [...acc, ...products];
        }
        return acc;
    }, [] as ProductModel[]);

    return {
        tabProducts,
        allProducts,
        errorMessage: getErrorMessage(state, ownProps.id),
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        pageType: getCurrentPage(state).type,
        productRecommendationWidgets: state.components.productRecommendations.widgets,
        session: state.context.session,
        itemReplacements: state.xItemReplacements,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    xItemReplacements,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    container?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    swiperStyles?: SwiperStyle;
    productCategoriesSelect?: SelectPresentationProps;
    carouselProductWrapper?: InjectableCss;
    productMiniCardStyles?: ProductMiniCardStyles;
}

// Xng-Changes: please check ProductCarouselExtensions.ts for styles
export const productCarouselStyles: ProductCarouselStyles = {};

const styles = productCarouselStyles;

const ProductCarousel = ({
    id,
    tabProducts,
    allProducts,
    fields,
    errorMessage,
    isTabletScreen,
    isMobileScreen,
    pageType,
    productRecommendationWidgets,
    session,
    shellContext,
    loadCarouselProducts,
    // XNG-CHANGES
    xItemReplacements,
    itemReplacements,
}: Props) => {
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const filteredTabs = tabProducts.filter(tab => tab.products.length);
    const [selectedTabOption, setSelectedTabOption] = useState(filteredTabs?.[0]?.title);
    // XNG-CHANGES
    const [replacementTitle, setReplacementTitle] = useState("");
    const itemReplacementRef: RefObject<any> = useRef(null);

    const productId = productContext?.product?.id;
    // XNG-CHANGES : adding carouselItemReplacement to show See Available Options in PDP
    useEffect(() => {
        if (!filteredTabs?.length || !replacementTitle) {
            xItemReplacements({
                id: productId,
                value: {
                    carouselItemReplacement: false,
                },
            });
            return;
        }
        let hasReplacementProducts = false;
        filteredTabs.forEach(filterTab => {
            if (filterTab.title === replacementTitle && filterTab?.products?.length > 0) {
                hasReplacementProducts = true;
            }
        });

        if (hasReplacementProducts) {
            xItemReplacements({
                id: productId,
                value: {
                    carouselItemReplacement: true,
                },
            });
        } else {
            xItemReplacements({
                id: productId,
                value: {
                    carouselItemReplacement: false,
                },
            });
        }
        setSelectedTabOption(filteredTabs[0].title);
    }, [filteredTabs?.length, replacementTitle]);
    // XNG-CHANGES : GUEST-2691
    useEffect(() => {
        if (filteredTabs[0]) {
            setSelectedTabOption(filteredTabs?.[0]?.title);
        }
    }, [filteredTabs?.length]);

    // XNG-CHANGES : to apply scroll behaviour in PDP for Available Options
    useEffect(() => {
        if (itemReplacements[productId]?.productItemReplacement && itemReplacementRef.current) {
            itemReplacementRef.current.scrollIntoView({ behavior: "smooth" });

            xItemReplacements({
                id: productId,
                value: {
                    productItemReplacement: false,
                },
            });
        }
    }, [itemReplacements[productId]?.productItemReplacement, itemReplacementRef.current]);

    useEffect(() => {
        fields.tabs.forEach(({ fields: tab }, index) => {
            loadCarouselProducts({
                isShellEdit: shellContext.isInShell && shellContext.isEditing,
                carouselId: `${id}-${tab.title}-${index}`,
                carouselType: tab.carouselType,
                relatedProductType: tab.relatedProductType,
                seedWithManuallyAssigned: tab.seedWithManuallyAssigned,
                displayProductsFrom: tab.displayProductsFrom,
                selectedCategoryIds: tab.selectedCategoryIds,
                numberOfProductsToDisplay: tab.numberOfProductsToDisplay,
                widgetPosition: tab.widgetPosition,
                isProductDetailsPage,
                productId,
                isProductListPage,
                category,
                isBrandDetailsPage,
                brand,
            });
            if (tab.relatedProductType === "Replacement") {
                setReplacementTitle(tab.title);
            }
        });
    }, [fields.tabs, productId, category?.id, brand?.id, productRecommendationWidgets, session?.pickUpWarehouse?.id]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (tabProducts.length === 0 || !allProducts || allProducts?.length === 0) {
        return null;
    }

    const handleSelectProductsTab: React.ChangeEventHandler<HTMLSelectElement> = event => {
        setSelectedTabOption(event.currentTarget.value);
    };

    if (isTabletScreen) {
        const tab = filteredTabs.find(tab => tab.title === selectedTabOption);
        if (!tab) {
            return null;
        }
        const productsByTab = tab?.products
            .map(id => allProducts.find(product => product.productNumber === id))
            .filter(elm => elm);
        const showNavigation = isMobileScreen ? productsByTab.length > 1 : productsByTab.length > 2;

        return (
            <StyledWrapper {...styles.container} ref={itemReplacementRef}>
                {!!fields.title?.length && <SectionTitle>{fields.title}</SectionTitle>}
                <StyledWrapper>
                    <Select onChange={handleSelectProductsTab} {...styles.productCategoriesSelect}>
                        {filteredTabs.map(tab => {
                            const value = tab.title;
                            return (
                                <option key={value} value={value}>
                                    {tab.title}
                                </option>
                            );
                        })}
                    </Select>
                    {/* TODO: add looping functionality */}
                    <XngCarousel
                        // loop
                        scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                        spaceBetween={22}
                        navigation={showNavigation}
                        draggable={isTouchDevice()}
                        extendedStyles={styles.swiperStyles}
                        breakpoints={{
                            480: {
                                slidesPerView: 1,
                            },
                            768: {
                                slidesPerView: 2,
                                spaceBetween: 20,
                            },
                            1024: {
                                slidesPerView: fields.slidesPerView,
                                spaceBetween: 20,
                            },
                        }}
                    >
                        {productsByTab?.map(product => (
                            <ProductMiniCard
                                key={product!.id}
                                product={product!}
                                widgetId={`${id}-${tab.title}-${tabProducts.indexOf(tab)}`}
                                showImage={fields.showImage}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAvailability={fields.showAvailability}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                extendedStyles={styles.productMiniCardStyles}
                            />
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container} ref={itemReplacementRef}>
            {!!fields.title?.length && <SectionTitle>{fields.title}</SectionTitle>}
            <TabGroup
                current={filteredTabs[0]?.title}
                {...styles.tabGroupStyle}
                id={`tab-group-${filteredTabs[0]?.title}`}
            >
                {filteredTabs.map(tab => {
                    const productsByTab = tab.products
                        .map(id => allProducts.find(product => product.productNumber === id))
                        .filter(elm => elm);
                    const showNavigation = (isMobileScreen && productsByTab.length > 1) || productsByTab.length > 4;
                    return (
                        <Tab key={tab.title} headline={tab.title} tabKey={tab.title} {...styles.tabStyle}>
                            {/* TODO: add looping functionality */}
                            {/* show the real scrollbar no need to set "dragSize" */}
                            <XngCarousel
                                scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                                spaceBetween={22}
                                navigation={showNavigation}
                                draggable={isTouchDevice()}
                                extendedStyles={styles.swiperStyles}
                                breakpoints={{
                                    480: {
                                        slidesPerView: 1,
                                    },
                                    768: {
                                        slidesPerView: 2,
                                        spaceBetween: 20,
                                    },
                                    1024: {
                                        slidesPerView: fields.slidesPerView,
                                        spaceBetween: 20,
                                    },
                                }}
                            >
                                {productsByTab?.map(product => (
                                    <ProductMiniCard
                                        key={product!.id}
                                        product={product!}
                                        widgetId={`${id}-${tab.title}-${tabProducts.indexOf(tab)}`}
                                        showImage={fields.showImage}
                                        showTitle={fields.showTitle}
                                        showPartNumbers={fields.showPartNumbers}
                                        showPrice={fields.showPrice}
                                        showAvailability={fields.showAvailability}
                                        showAddToCart={fields.showAddToCart}
                                        showAddToList={fields.showAddToList}
                                        showImageBadges={fields.showImageBadges}
                                        maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                        showTextBadges={fields.showTextBadges}
                                        maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                        extendedStyles={styles.productMiniCardStyles}
                                    />
                                ))}
                            </XngCarousel>
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const slidesPerViewOptions = [
    { value: 1, displayName: "1" },
    { value: 2, displayName: "2" },
    { value: 3, displayName: "3" },
    { value: 4, displayName: "4" },
    { value: 5, displayName: "5" },
    { value: 6, displayName: "6" },
];

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.slidesPerView,
                displayName: "Slides Per View",
                editorTemplate: "DropDownField",
                options: slidesPerViewOptions,
                defaultValue: 4,
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: item => {
                    return item.fields.title;
                },
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tabFields.carouselType,
                        displayName: "Carousel Type",
                        editorTemplate: "DropDownField",
                        options: carouselTypeOptions,
                        defaultValue: "featuredProducts",
                        customFilter: (item, _, settings) => {
                            const {
                                settingsCollection: {
                                    websiteSettings: { enableDynamicRecommendations },
                                    productSettings: { enableProductRecommendations },
                                },
                            } = settings;

                            if (!enableDynamicRecommendations && item.value === "topSellers") {
                                return false;
                            }

                            if (!enableProductRecommendations && item.value === "recommendedProducts") {
                                return false;
                            }

                            return true;
                        },
                    },
                    {
                        name: tabFields.displayProductsFrom,
                        displayName: "Display Products From",
                        editorTemplate: "DropDownField",
                        options: [
                            { value: "allCategories", displayName: "All Categories" },
                            { value: "selectedCategories", displayName: "Selected Categories" },
                            { value: "customerSegments", displayName: "Customer Segments" },
                        ],
                        defaultValue: "allCategories",
                        isVisible: item => item.fields[tabFields.carouselType] === "topSellers",
                    },
                    {
                        name: tabFields.selectedCategoryIds,
                        displayName: "Selected Categories",
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                        isVisible: item =>
                            item.fields[tabFields.carouselType] === "topSellers" &&
                            item.fields[tabFields.displayProductsFrom] === "selectedCategories",
                    },
                    {
                        name: tabFields.relatedProductType,
                        displayName: "Related Product Type",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "ProductRelationship",
                        defaultValue: "",
                        isVisible: item => item.fields[tabFields.carouselType] === "relatedProducts",
                    },
                    {
                        name: tabFields.numberOfProductsToDisplay,
                        displayName: "Number Of Products To Display",
                        editorTemplate: "IntegerField",
                        min: 1,
                        max: 20,
                        defaultValue: 10,
                        isVisible: item =>
                            item.fields[tabFields.carouselType] === "customersAlsoPurchased" ||
                            item.fields[tabFields.carouselType] === "topSellers",
                    },
                    {
                        name: tabFields.seedWithManuallyAssigned,
                        displayName: "Seed With Manually Assigned",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "ProductRelationship",
                        defaultValue: "",
                        isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                            pageType === "ProductDetailsPage" &&
                            item.fields[tabFields.carouselType] === "customersAlsoPurchased",
                    },
                    {
                        name: tabFields.widgetPosition,
                        displayName: "Widget Position",
                        editorTemplate: "IntegerField",
                        min: 1,
                        defaultValue: 1,
                        isVisible: item => item.fields[tabFields.carouselType] === "recommendedProducts",
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
